export const DB_NAME = 'bahlus-audio-offline';
export const DB_VERSION = 5;

let dbPromise = null;
/** Serialize IDB work — concurrent reads/writes caused flaky partial playlist lists (esp. mobile Safari). */
let storeChain = Promise.resolve();

function resetDbConnection() {
  dbPromise = null;
}

export function openAudioDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resetDbConnection();
      reject(new Error('Audio storage is not available in this browser.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => {
      resetDbConnection();
      reject(req.error || new Error('Could not open audio storage'));
    };
    req.onblocked = () => {
      console.warn('[audio-player] IndexedDB upgrade blocked — close other tabs for this site.');
    };
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('tracks')) {
        const store = db.createObjectStore('tracks', { keyPath: 'key' });
        store.createIndex('savedAt', 'savedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains('playlists')) {
        const pl = db.createObjectStore('playlists', { keyPath: 'id' });
        pl.createIndex('savedAt', 'savedAt', { unique: false });
        pl.createIndex('name', 'name', { unique: false });
      }
      if (!db.objectStoreNames.contains('browseCategories')) {
        const cat = db.createObjectStore('browseCategories', { keyPath: 'id' });
        cat.createIndex('savedAt', 'savedAt', { unique: false });
        cat.createIndex('name', 'name', { unique: false });
      }
      if (!db.objectStoreNames.contains('genreStations')) {
        const gs = db.createObjectStore('genreStations', { keyPath: 'id' });
        gs.createIndex('genreId', 'genreId', { unique: false });
        gs.createIndex('savedAt', 'savedAt', { unique: false });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        resetDbConnection();
      };
      db.onclose = () => resetDbConnection();
      resolve(db);
    };
  });

  return dbPromise;
}

function runAudioStoreImmediate(storeName, mode, fn) {
  return openAudioDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        let tx;
        try {
          tx = db.transaction(storeName, mode);
        } catch (e) {
          reject(e);
          return;
        }
        const store = tx.objectStore(storeName);
        let result;
        try {
          result = fn(store);
        } catch (e) {
          reject(e);
          return;
        }
        const finish = (value) => resolve(value);
        const fail = (err) => reject(err || new Error('Audio storage transaction failed'));
        tx.oncomplete = () => {
          if (result != null && typeof result.then === 'function') {
            result.then(finish).catch(fail);
          } else {
            finish(result);
          }
        };
        tx.onerror = () => fail(tx.error);
        tx.onabort = () => fail(tx.error);
      })
  );
}

export function runAudioStore(storeName, mode, fn) {
  const next = storeChain.then(
    () => runAudioStoreImmediate(storeName, mode, fn),
    () => runAudioStoreImmediate(storeName, mode, fn)
  );
  storeChain = next.catch(() => {});
  return next;
}
